import { useState } from 'react';
import auth from '@mp/utils/firebase/firebase.web';
import {
  MultiFactorError,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  UserCredential,
  getMultiFactorResolver,
  multiFactor,
} from 'firebase/auth';

let globalRecaptchaVerifier: RecaptchaVerifier | null = null;

let renderAttempts = 0;

const forceReload = () => {
  console.warn('Forcing page reload...');

  try {
    // Method 1: Standard reload
    window.location.reload();
  } catch (e1) {
    console.warn('Standard reload failed:', e1);
    try {
      // Method 2: Redirect to current URL
      window.location.href = window.location.href;
    } catch (e2) {
      console.error('All reload methods failed:', e2);

      // Method 3: Replace current location
      window.location.replace(window.location.href);
    }
  }
};

const getGlobalRecaptchaVerifier = async (): Promise<RecaptchaVerifier> => {
  if (
    !globalRecaptchaVerifier ||
    //@ts-ignore private
    globalRecaptchaVerifier.destroyed ||
    //@ts-ignore private
    (globalRecaptchaVerifier.renderPromise &&
      //@ts-ignore private
      globalRecaptchaVerifier.renderPromise.constructor.name === 'Promise' &&
      //@ts-ignore private
      globalRecaptchaVerifier.recaptcha === null)
  ) {
    renderAttempts++;

    if (renderAttempts > 2) {
      forceReload();
      throw new Error('Render failed multiple times');
    }

    // Clear any existing hanging verifier
    //@ts-ignore private
    if (globalRecaptchaVerifier && !globalRecaptchaVerifier.destroyed) {
      try {
        globalRecaptchaVerifier.clear();
      } catch (e) {
        console.warn('Error clearing previous verifier:', e);
      }
    }

    // Make sure container exists
    let container = document.getElementById('r-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'r-container';
      container.style.display = 'none';
      document.body.appendChild(container);
    }

    globalRecaptchaVerifier = new RecaptchaVerifier('r-container', { size: 'invisible' }, auth);

    try {
      await Promise.race([
        globalRecaptchaVerifier.render(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Render timeout')), 5000)),
      ]);
    } catch (error) {
      console.error('Render failed:', error);
      // Force page reload as last resort
      window.location.reload();
      throw error;
    }
  }
  return globalRecaptchaVerifier;
};

export const useMFAEnrollment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const phoneAuthProvider = new PhoneAuthProvider(auth);

  const startEnrollment = async (phoneNumber: string) => {
    const recaptchaVerifier = await getGlobalRecaptchaVerifier();

    try {
      setLoading(true);
      setError(null);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not signed in');
      }

      const session = await multiFactor(user).getSession();

      const phoneInfoOptions = {
        phoneNumber,
        session,
      };

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifier,
      );

      return verificationId;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error('Unexpected error'));
      }
      // Don't clear the singleton - let it be reused
      return null;
    } finally {
      setLoading(false);
    }
  };

  const enrollPhoneMfa = async (verificationCode: string, verificationId: string) => {
    if (!verificationId) {
      throw new Error('No verification ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not signed in');
      }

      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);

      await multiFactor(user).enroll(assertion, 'Phone number');
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
        throw err;
      } else {
        const fallback = new Error('Unexpected error');
        setError(fallback);
        throw fallback;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaChallenge = async (
    error: MultiFactorError,
  ): Promise<{
    verificationId: string;
    resolver: MultiFactorResolver;
  } | null> => {
    const recaptchaVerifier = await getGlobalRecaptchaVerifier();
    try {
      setLoading(true);
      setError(null);

      const resolver = getMultiFactorResolver(auth, error);
      const hint = resolver.hints[0];
      const session = resolver.session;

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          multiFactorHint: hint,
          session: session,
        },
        recaptchaVerifier,
      );

      return { verificationId, resolver };
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error('Unexpected error during MFA challenge'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getUserPhoneNumberMasked = (error: MultiFactorError): string | undefined => {
    const resolver = getMultiFactorResolver((error as any).auth, error);

    const phoneHint = resolver.hints.find(
      (hint): hint is PhoneMultiFactorInfo => 'phoneNumber' in hint,
    );

    if (phoneHint?.phoneNumber) {
      return `(•••) •••-${phoneHint.phoneNumber.slice(-4)}`;
    }

    return undefined;
  };

  const resolveMfaSignIn = async (
    code: string,
    verificationId: string,
    resolver: MultiFactorResolver,
  ): Promise<UserCredential> => {
    try {
      setLoading(true);
      setError(null);

      const credential = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);

      return await resolver.resolveSignIn(assertion);
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error('Unexpected error resolving MFA'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    startEnrollment,
    enrollPhoneMfa,
    handleMfaChallenge,
    resolveMfaSignIn,
    getUserPhoneNumberMasked,
    loading,
    error,
  };
};
